import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { tiAsset } from '../data/tripinkAssets';

/* Book Now = the real 1Dash.Pro form "Trip Ink: Full-Day Booking Request" (form id cjV3SwTmgojNFYOXblG6),
   embedded with the official inline iframe + form_embed.js. Submissions create/update the contact in
   Trip Ink's 1Dash.Pro account. The site's Book / Claim It buttons link here with ?artist= and ?design=;
   those are passed into the form through its query keys (artist, design, utm_source).
   Note: the 1Dash.Pro form turns "+" into a space, so "+" in design names is sent as "&". */
const FORM_ID = 'cjV3SwTmgojNFYOXblG6';
const FORM_SRC = `https://data.1dash.pro/widget/form/${FORM_ID}`;
const EMBED_JS = 'https://data.1dash.pro/js/form_embed.js';
const ARTISTS: Record<string, string> = {
  rick: 'Rick Trip',
  'rick-trip': 'Rick Trip',
  'rick trip': 'Rick Trip',
  rich: 'Rich Pineda',
  'rich-pineda': 'Rich Pineda',
  'rich pineda': 'Rich Pineda',
  brian: 'Brian',
  sophia: 'Sophia Mae',
  'sophia-mae': 'Sophia Mae',
  'sophia mae': 'Sophia Mae',
  valerio: 'Dr. Valerio',
  'dr-valerio': 'Dr. Valerio',
  'dr. valerio': 'Dr. Valerio',
  'dr valerio': 'Dr. Valerio',
  casso: 'Casso',
  chris: 'Chris',
  any: 'Any artist',
  'any artist': 'Any artist'
};
const enc = (v: string) => encodeURIComponent(v.replace(/\+/g, '&'));
export function BookNow() {
  const {
    search
  } = useLocation();
  const {
    src,
    artist,
    design
  } = useMemo(() => {
    const q = new URLSearchParams(search);
    const rawArtist = (q.get('artist') || '').trim();
    const artistName = ARTISTS[rawArtist.toLowerCase()] || '';
    const designName = (q.get('design') || '').trim().slice(0, 120);
    const utm = (q.get('utm_source') || '').trim().slice(0, 80);
    const parts: string[] = [];
    if (artistName) parts.push(`artist=${enc(artistName)}`);
    if (designName) parts.push(`design=${enc(designName)}`);
    if (utm) parts.push(`utm_source=${enc(utm)}`);
    return {
      src: parts.length ? `${FORM_SRC}?${parts.join('&')}` : FORM_SRC,
      artist: artistName,
      design: designName
    };
  }, [search]);
  useEffect(() => {
    document.title = 'Book Your Full Day | Trip Ink Tattoo Las Vegas';
    if (!document.querySelector(`script[src="${EMBED_JS}"]`)) {
      const s = document.createElement('script');
      s.src = EMBED_JS;
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);
  return <>
      <PageHero title="Book Your Full Day" subtitle="Full-day sessions" ghost="BOOK" image={tiAsset('w-7118.jpg')} data-id="element-345" data-mp="38hsj7alhr9se">
        <p className="tis-sub" data-id="element-346" data-mp="2trtlta1rg2dz">Tell us your idea and pick your artist. We will reach out to confirm your artist, your dates and your deposit.</p>
        {(artist || design) && <div className="mt-6 flex flex-wrap justify-center gap-3" data-id="element-347" data-mp="3mh0fnvq9s3sw">
            {artist && <span className="rounded-full border border-gold/70 px-4 py-1.5 font-slab text-sm font-extrabold uppercase tracking-[0.14em] text-gold-2" data-id="element-348" data-mp="3r8a4wv7wp549">Artist: {artist}</span>}
            {design && <span className="rounded-full border border-neon/70 px-4 py-1.5 font-slab text-sm font-extrabold uppercase tracking-[0.14em] text-neon-2" data-id="element-349" data-mp="1at5f2bx4v9ta">Design: {design}</span>}
          </div>}
      </PageHero>

      <section className="px-6 pb-[clamp(60px,6vw,160px)]" data-id="element-350" data-mp="1klupd3gy6hn1">
        <div className="grid gap-[clamp(24px,3vw,90px)] lg:grid-cols-[minmax(0,1.6fr)_minmax(300px,1fr)]" data-id="element-351" data-mp="wv731d6abpgy">
          <div className="tis-panel overflow-hidden p-[clamp(6px,.8vw,18px)]" data-id="element-352" data-mp="rwdzacgsvctz">
            <iframe key={src} src={src} style={{
            width: '100%',
            height: '1816px',
            border: 'none',
            borderRadius: 18,
            display: 'block'
          }} id={`inline-${FORM_ID}`} data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Trip Ink: Full-Day Booking Request" data-height="1816" data-layout-iframe-id={`inline-${FORM_ID}`} data-form-id={FORM_ID} data-cookie-consent="true" data-cookie-consent-provider="auto" title="Trip Ink: Full-Day Booking Request" data-id="element-353" data-mp="15v3wrt7j7eiv" />
          </div>

          <aside className="space-y-[clamp(18px,1.6vw,40px)] lg:sticky lg:top-28 lg:h-fit" data-id="element-354" data-mp="3vi9qlgaifkna">
            <div className="tis-card p-[clamp(20px,1.8vw,40px)]" data-id="element-355" data-mp="44405dhg0v9l">
              <span className="tis-eyebrow" data-id="element-356" data-mp="46qa1jrt3muz">How it works</span>
              <ol className="mt-4 space-y-4" data-id="element-357" data-mp="92xfzck7cycx">
                {[['Send your request', 'Your idea, your artist and the dates that work for you.'], ['We call or text you back', 'We confirm the artist, the design and how many days it takes.'], ['Lock in your day', 'A deposit holds your date. We confirm the amount with you.']].map(([t, d], i) => <li key={t} className="flex gap-4" data-id="element-358" data-mp="1ymoz8ygcy6u5" data-mpi={i}>
                    <span className="font-slab text-[clamp(2rem,2vw,3.4rem)] font-black leading-none text-neon" data-id="element-359" data-mp="1tmkegw8f2py4">{i + 1}</span>
                    <span data-id="element-360" data-mp="1onzgl3gn0icu">
                      <b className="block font-slab text-[clamp(1.2rem,.8vw+.8rem,2rem)] font-extrabold uppercase tracking-[0.04em] text-white" data-id="element-361" data-mp="1ooram9ul6810">{t}</b>
                      <span className="text-ink-muted" data-id="element-362" data-mp="2hc52htjfgde9">{d}</span>
                    </span>
                  </li>)}
              </ol>
            </div>
            <div className="tis-card tis-contact p-[clamp(20px,1.8vw,40px)]" data-id="element-363" data-mp="27lk15ycdcurn">
              <span className="tis-eyebrow" data-id="element-364" data-mp="27jfzln1y07uj">Rather talk?</span>
              <div className="mt-3" data-id="element-365" data-mp="352927pthvszz">
                <a href="tel:702-605-6633" data-id="element-366" data-mp="352hd5c624o8j">
                  <b data-id="element-367" data-mp="308wpoe23bcnl">CALL</b>702-605-6633
                </a>
                <a href="tel:702-586-5308" data-id="element-368" data-mp="yce1cdi9r08w">
                  <b data-id="element-369" data-mp="ydp7ul33mm3p">WALK-IN</b>702-586-5308
                </a>
                <a href="sms:702-408-8305" data-id="element-370" data-mp="12dr9wvcqxhji">
                  <b data-id="element-371" data-mp="12dhjx7rpbadt">TEXT</b>702-408-8305
                </a>
                <a href="mailto:tripinklv@gmail.com" data-id="element-372" data-mp="on4b736zjrnf">
                  <b data-id="element-373" data-mp="omchjxwy83zs">EMAIL</b>tripinklv@gmail.com
                </a>
              </div>
            </div>
            <div className="tis-card p-[clamp(20px,1.8vw,40px)]" data-id="element-374" data-mp="tjkxdrumjvxv">
              <span className="tis-eyebrow" data-id="element-375" data-mp="enn88tj4brv5">Save time at the shop</span>
              <p className="mt-2 text-ink-muted" data-id="element-376" data-mp="jo165ir09lfg">Fill out your release form before your session.</p>
              <Link to="/release-form" className="tis-btn sm mt-5" data-id="element-377" data-mp="5o9tpmg50d3k">
                Release Form
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </>;
}